import { useState } from 'react';
import { useQuoteStore, useQuoteSummary } from '../../store/useQuoteStore';
import { GLOBAL_SETTINGS } from '../../data/settings';
import { calcItemTotal } from '../../utils/pricing';
import { useQuoteCheckout } from '../../hooks/useQuoteCheckout';
import SavedQuoteResultPanel from './SavedQuoteResultPanel';
import { dialog, NumericInput } from '../ui';
import { DEPOSIT_OPTIONS, snapDepositPercent } from '../../utils/depositOptions';
import { requiresDiscountAuthorisation, DISCOUNT_WARN_RATIO } from '../../utils/discountGuard';
import { reverseFinalTotalToDiscount } from '../../utils/finalTotalReverseMath';
import { round2 } from '../../../shared/money';

export default function CheckoutSidebar() {
  const cbdSurcharge = useQuoteStore((state) => state.cbdSurcharge);
  const setCbdSurcharge = useQuoteStore((state) => state.setCbdSurcharge);
  const deliveryWaived = useQuoteStore((state) => state.deliveryWaived);
  const setDeliveryWaived = useQuoteStore((state) => state.setDeliveryWaived);
  const items = useQuoteStore((state) => state.items);
  const activeQuoteId = useQuoteStore((state) => state.activeQuoteId);
  const resetWorkspace = useQuoteStore((state) => state.resetWorkspace);
  const issuedCompany = useQuoteStore((state) => state.issuedCompany);
  const depositPercent = useQuoteStore((state) => state.depositPercent);
  const setDepositPercent = useQuoteStore((state) => state.setDepositPercent);
  const interiorDesign = useQuoteStore((state) => state.interiorDesign);
  const setInteriorDesign = useQuoteStore((state) => state.setInteriorDesign);
  const setLumpSumDiscount = useQuoteStore((state) => state.setLumpSumDiscount);
  const orderRemarks = useQuoteStore((state) => state.orderRemarks);
  const setOrderRemarks = useQuoteStore((state) => state.setOrderRemarks);
  const pepperCloudOpportunityId = useQuoteStore((state) => state.pepperCloudOpportunityId);
  const setPepperCloudOpportunityId = useQuoteStore((state) => state.setPepperCloudOpportunityId);
  const summary = useQuoteSummary();

  const checkout = useQuoteCheckout();

  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [customTotal, setCustomTotal] = useState<string>('');

  const handleApplyCustomTotal = async () => {
    const targetGrandTotal = parseFloat(customTotal);
    if (!isNaN(targetGrandTotal) && targetGrandTotal >= 0) {
      // Back-solve the discount that lands on the entered target. The GST
      // divisor follows summary.hasGst — only WSS charges GST, so for Smart
      // Renovation / Pinki Beans the target IS the final price (no 1.09 strip).
      const requiredDiscount = reverseFinalTotalToDiscount({
        targetGrandTotal,
        effectiveItemsTotal: summary.effectiveItemsTotal,
        deliveryFee: summary.deliveryFee,
        cbdSurchargeFee: summary.cbdSurchargeFee,
        hasGst: summary.hasGst,
        gstRate: GLOBAL_SETTINGS.gstRate,
      });

      if (requiresDiscountAuthorisation(requiredDiscount, summary.effectiveItemsTotal)) {
        const authorise = await dialog.confirm({
          title: 'Authorise large discount?',
          message: `The requested final price requires a discount of $${requiredDiscount.toFixed(2)}, which exceeds ${Math.round(DISCOUNT_WARN_RATIO * 100)}% of the Base Items Total.`,
          confirmLabel: 'Authorise',
          tone: 'destructive',
        });
        if (!authorise) {
          setLumpSumDiscount(0);
          setIsEditingTotal(false);
          return;
        }
      }

      setLumpSumDiscount(Math.max(0, requiredDiscount));
    }
    setIsEditingTotal(false);
  };

  const handleGenerate = (target: 'DRAFT' | 'SENT') => {
    checkout.dismissUrl();
    checkout.clearError();
    void checkout.executeSave(target, summary);
  };

  return (
    <aside className="lg:col-span-5 sticky top-28">
      <div className="bg-secondary rounded-xl p-8 shadow-2xl relative overflow-hidden">
        {/* WSS Orange accent bar */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-primary"></div>
        <h2 className="text-xl font-bold font-headline text-white mb-6 flex items-center gap-2">
          <span className="material-symbols-outlined">calculate</span>
          Quotation Preview
        </h2>

        {activeQuoteId && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded p-3 flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold uppercase tracking-widest text-red-300">
                EDITING EXISTING QUOTE
              </span>
              <span className="text-[10px] font-mono font-bold text-red-200 bg-red-500/20 px-2 py-0.5 rounded">
                {activeQuoteId.split('-')[0].toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-red-100/70 font-medium leading-tight mb-1">
              Saving will replace the existing quote with these changes.
            </p>
            <button
              onClick={() =>
                void (async () => {
                  const ok = await dialog.confirm({
                    title: 'Discard this quote?',
                    message: 'Unsaved changes will be lost and you’ll return to a blank workspace.',
                    confirmLabel: 'Discard',
                    tone: 'destructive',
                  });
                  if (ok) resetWorkspace();
                })()
              }
              className="text-xs font-bold uppercase text-white bg-red-500 hover:bg-red-600 py-2 rounded transition-colors w-full tracking-wider shadow-sm"
            >
              Discard and Start Fresh
            </button>
          </div>
        )}

        <div className="space-y-4">
          {items.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-3 mb-6 max-h-[300px] overflow-y-auto custom-scrollbar">
              {items.map((item, idx) => {
                if (!item.product && item.source !== 'adhoc') return null;
                const combinedTotal = calcItemTotal(item);
                const room =
                  item.room === 'Other' ? (item.otherRoom ?? '').trim() : (item.room ?? '').trim();
                return (
                  <div
                    key={item.id}
                    className="pb-3 border-b border-white/10 last:border-0 last:pb-0"
                  >
                    <div className="flex gap-2 items-baseline">
                      <span className="text-primary font-bold text-xs w-4">{idx + 1}.</span>
                      <div className="flex-1">
                        {room && (
                          <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-0.5">
                            {room}
                          </p>
                        )}
                        <div className="flex justify-between items-baseline gap-4">
                          <p className="text-sm font-bold text-white leading-tight flex items-baseline justify-between gap-4 flex-1">
                            <span>
                              {item.source === 'adhoc'
                                ? item.adhocDescription || 'Custom item'
                                : item.product?.quote_desc}
                            </span>
                            <span className="whitespace-nowrap">Qty: {item.qty}</span>
                          </p>
                          <span className="font-bold text-white text-sm leading-tight whitespace-nowrap pl-2">
                            ${combinedTotal.toFixed(2)}
                          </span>
                        </div>

                        {item.widthMm && (
                          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-white/50">
                            {item.widthMm} W x {item.heightMm} H (mm)
                          </p>
                        )}

                        {item.colour?.trim() && (
                          <p className="mt-0.5 text-[11px] text-white/60">
                            Colour: <span className="font-semibold">{item.colour.trim()}</span>
                          </p>
                        )}

                        {item.addons && item.addons.length > 0 && (
                          <div className="mt-2 pl-2 border-l border-primary/30 space-y-1">
                            {item.addons.map((addon) => (
                              <p key={addon.id} className="text-xs text-white/70 font-medium">
                                + {addon.product.quote_desc}
                                {addon.colour?.trim() && (
                                  <span className="text-white/50"> — {addon.colour.trim()}</span>
                                )}
                              </p>
                            ))}
                          </div>
                        )}

                        {item.remarks?.trim() && (
                          <p className="mt-1 text-[11px] italic leading-snug text-white/50 whitespace-pre-line">
                            {item.remarks.trim()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-between items-center bg-white/5 p-3 rounded-lg border border-white/10 mb-2">
            <span className="text-white/80 text-sm font-medium">CBD Surcharge Zone?</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={cbdSurcharge}
                onChange={(e) => setCbdSurcharge(e.target.checked)}
              />
              <div className="w-9 h-5 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex justify-between text-white/70 text-sm font-medium pt-2">
            <span>Base Items Total</span>
            <span className="font-bold text-white">${summary.itemsTotal.toFixed(2)}</span>
          </div>

          {summary.minChargeApplied && (
            <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg border border-primary/50">
              <div className="flex items-center gap-2 text-primary">
                <span className="material-symbols-outlined text-sm">info</span>
                <span className="font-bold text-xs uppercase tracking-wider">
                  Minimum Order Top-up
                </span>
              </div>
              <span className="font-bold text-primary">
                +$ {(GLOBAL_SETTINGS.minOrderCharge - summary.itemsTotal).toFixed(2)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center text-white/70 text-sm font-medium">
            <div className="flex items-center gap-2">
              <span>Delivery Fee</span>
              {!deliveryWaived && (
                <button
                  onClick={() => setDeliveryWaived(true)}
                  className="text-white/40 hover:text-red-400 transition-colors flex items-center"
                  title="Waive Delivery Fee"
                >
                  <span className="material-symbols-outlined text-[14px]">delete</span>
                </button>
              )}
              {deliveryWaived && (
                <span className="text-[9px] bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded font-bold uppercase tracking-widest mt-px">
                  Waived
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {deliveryWaived && (
                <button
                  onClick={() => setDeliveryWaived(false)}
                  className="text-[10px] bg-white/10 hover:bg-white/20 text-white transition-colors px-2 py-1 rounded uppercase font-bold tracking-wider relative z-10 cursor-pointer"
                >
                  Restore
                </button>
              )}
              <span
                className={`font-bold ${deliveryWaived ? 'text-white/40 line-through' : 'text-white'}`}
              >
                ${GLOBAL_SETTINGS.deliveryFee.toFixed(2)}
              </span>
            </div>
          </div>

          {cbdSurcharge && (
            <div className="flex justify-between text-white/70 text-sm font-medium">
              <span>CBD Zone Surcharge</span>
              <span className="font-bold text-white">${summary.cbdSurchargeFee.toFixed(2)}</span>
            </div>
          )}

          {round2(summary.appliedDiscount) > 0 && (
            <div className="flex justify-between text-green-400 text-sm font-bold border-t border-white/10 pt-4 mt-4">
              <span className="uppercase tracking-wider">Lump Sum Discount</span>
              <span>-${summary.appliedDiscount.toFixed(2)}</span>
            </div>
          )}

          <div
            className={`flex justify-between text-white/70 text-sm font-medium ${round2(summary.appliedDiscount) > 0 ? 'pt-2' : 'border-t border-white/10 pt-4 mt-4'}`}
          >
            <span>Subtotal</span>
            <span className="font-bold text-white">${summary.subtotal.toFixed(2)}</span>
          </div>

          {issuedCompany === 'WSS Windows & Doors' && (
            <div className="flex justify-between text-white/70 text-sm font-medium pb-2 border-b border-white/10">
              <span>{Math.round(GLOBAL_SETTINGS.gstRate * 100)}% GST</span>
              <span className="font-bold text-white">${summary.gst.toFixed(2)}</span>
            </div>
          )}

          <div className="pt-4 flex flex-col items-end border-t border-white/10 mt-4">
            <div className="w-full flex justify-between items-center mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">
                Grand Total (Estimate)
              </span>
              {!isEditingTotal && (
                <button
                  onClick={() => {
                    setCustomTotal(summary.grandTotal.toFixed(2));
                    setIsEditingTotal(true);
                  }}
                  className="text-[10px] text-white/40 hover:text-white underline uppercase"
                >
                  Edit Final Price
                </button>
              )}
            </div>
            {isEditingTotal ? (
              <div className="flex gap-2 items-center mt-2 w-full justify-end">
                <span className="text-xl font-bold text-white/70">$</span>
                <NumericInput
                  decimal
                  value={customTotal}
                  onChange={setCustomTotal}
                  className="bg-white/10 border border-white/20 text-white font-extrabold text-3xl font-headline w-40 text-right p-2 rounded outline-none focus:border-primary"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleApplyCustomTotal();
                  }}
                />
                <button
                  onClick={() => void handleApplyCustomTotal()}
                  className="bg-primary hover:bg-primary/90 text-white p-2 rounded-lg material-symbols-outlined ml-2"
                >
                  check
                </button>
                <button
                  onClick={() => {
                    setIsEditingTotal(false);
                    setLumpSumDiscount(0);
                  }}
                  className="text-white/40 hover:text-white p-2 material-symbols-outlined shrink-0 text-sm"
                >
                  close
                </button>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold font-headline text-white tracking-tighter">
                  ${summary.grandTotal.toFixed(2)}
                </span>
              </div>
            )}
            <p className="text-[11px] text-white/40 mt-3 text-right font-medium">
              Includes labor, materials, and site check.
            </p>
          </div>

          <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-0.5">
                <span className="text-white text-sm font-bold">
                  Require Deposit at Confirmation
                </span>
                <span className="text-white/50 text-[11px] font-medium leading-snug">
                  If off, the customer is asked for the full grand total instead of a part-payment.
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={depositPercent != null}
                  onChange={(e) =>
                    setDepositPercent(e.target.checked ? (depositPercent ?? 50) : undefined)
                  }
                />
                <div className="w-9 h-5 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            {depositPercent != null && (
              <div className="mt-4 flex items-center gap-3">
                <select
                  value={snapDepositPercent(depositPercent)}
                  onChange={(e) => setDepositPercent(parseInt(e.target.value, 10))}
                  className="bg-white/10 border border-white/20 text-white font-extrabold text-2xl font-headline w-24 text-center p-2 rounded outline-none focus:border-primary [&>option]:text-secondary"
                >
                  {DEPOSIT_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}%
                    </option>
                  ))}
                </select>
                <span className="text-white/70 text-sm font-medium">of Grand Total</span>
                <span className="ml-auto font-bold text-primary text-lg font-headline">
                  ${((summary.grandTotal * snapDepositPercent(depositPercent)) / 100).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-0.5">
                <span className="text-white text-sm font-bold">Interior Design order</span>
                <span className="text-white/50 text-[11px] font-medium leading-snug">
                  The customer is not asked to book a site measurement after paying. Staff can still
                  create one from the order.
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={interiorDesign}
                  onChange={(e) => setInteriorDesign(e.target.checked)}
                />
                <div className="w-9 h-5 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>

          <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex flex-col gap-0.5 mb-3">
              <span className="text-white text-sm font-bold">Remarks</span>
              <span className="text-white/50 text-[11px] font-medium leading-snug">
                Applies to the whole quotation — prints on the customer quotation and carries to the
                order confirmation and invoice. Leave blank if not needed.
              </span>
            </div>
            <textarea
              value={orderRemarks}
              onChange={(e) => setOrderRemarks(e.target.value)}
              rows={3}
              placeholder="e.g. Lead time 6 weeks from confirmation. Price excludes hacking works."
              className="w-full bg-white/10 border border-white/20 text-white text-sm rounded p-2.5 outline-none focus:border-primary placeholder:text-white/30 resize-y"
            />
          </div>

          <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex flex-col gap-0.5 mb-3">
              <span className="text-white text-sm font-bold">PepperCloud opportunity</span>
              <span className="text-white/50 text-[11px] font-medium leading-snug">
                Optional. Paste the opportunity id from PepperCloud and the order value will be
                written back there on confirmation. Leave blank to skip.
              </span>
            </div>
            <input
              type="text"
              value={pepperCloudOpportunityId}
              onChange={(e) => setPepperCloudOpportunityId(e.target.value)}
              placeholder="e.g. 6a574e86b42d23249900cc73"
              className="w-full bg-white/10 border border-white/20 text-white text-sm rounded p-2.5 outline-none focus:border-primary placeholder:text-white/30 font-mono"
            />
          </div>

          {checkout.actionError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-200 mt-4 flex items-start gap-2 shadow-sm">
              <span className="material-symbols-outlined text-[16px] mt-0.5 text-red-400">
                error
              </span>
              <span className="font-medium leading-snug">{checkout.actionError}</span>
            </div>
          )}

          {checkout.savedQuoteUrl && (
            <SavedQuoteResultPanel url={checkout.savedQuoteUrl} onDismiss={checkout.dismissUrl} />
          )}

          <div className="mt-6 flex flex-col gap-3">
            <button
              className="w-full bg-primary text-white py-4 rounded-lg font-bold text-lg font-headline shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider relative overflow-hidden"
              disabled={summary.grandTotal === 0 || checkout.isGenerating}
              onClick={() => handleGenerate('DRAFT')}
            >
              <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent)] -translate-x-[100%] hover:animate-[shimmer_1.5s_infinite]"></div>
              {checkout.isGenerating ? 'Generating...' : 'Generate & Review Quotation'}
            </button>
            <button
              className="w-full bg-white/10 text-white py-3.5 rounded-lg font-bold text-sm font-headline hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider border border-white/20"
              disabled={summary.grandTotal === 0 || checkout.isGenerating}
              onClick={() => handleGenerate('SENT')}
            >
              {checkout.isGenerating ? 'Processing...' : 'Skip Review & Send Immediately'}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 p-6 bg-white border border-outline-variant rounded-xl flex items-start gap-4 shadow-sm">
        <span className="material-symbols-outlined text-secondary mt-0.5">verified_user</span>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold text-secondary uppercase tracking-wider">
            Quotation Notes
          </p>
          <p className="text-xs text-outline leading-relaxed font-medium">
            Saved quotes live in our secure system. This is a quotation tool only — no invoice is
            raised until the final contract is signed. Quotes are valid for 30 days from the issue
            date.
          </p>
        </div>
      </div>
    </aside>
  );
}
